import React, { useState } from 'react';
import * as yup from 'yup';
import FirstUpper from '../../FirstUpper';
import { useAppContext } from '../../../Services/AppContext';
import PriceHintModal from '../Hint/PriceHintModal';
import Tooltip from '../../Popup/Tooltip';
import HealthPlanPriceResult, {
	getAppointmentsCount,
	ResultType,
} from '../../../Modules/HealthPlan/Components/Pricing/HealthPlanPriceResult';
import { useHealthPlanContext } from '../../../Services/HealthPlanContext';
import { useEffectAsync } from '../../../helpers';
import apiClient from '../../../Services/ApiClient';
import Label from '../../Base/Label/Label';
import Checkbox from '../../Base/Checkbox/Checkbox';
import SubmitButton from '../../Button/SubmitButton';
import {
	HealthPlanUser,
	SubscriptionItem,
	useLazyHealthPlanUserItemIdQuery,
	useLazyHealthPlanUserQuery,
} from '../../../graphql/graphql';
import { joinClasses } from '../../../Utils/className.util';
import ScrollContainer from '../../../Modules/Base/Components/ScrollContainer';
import useTranslation from '../../../Hooks/useTranslation';
// @ts-ignore
import styles from './HealthPlanPricing.module.scss';
import useCurrency from '../../../Modules/Region/Hooks/useCurrency';
import FormattedPriceInputField from '../../../Modules/Base/Components/Inputs/FormattedPriceInput/FormattedPriceInputField';
import { HealthPlanCode } from '../../../Enums/HealthPlanCode.enum';
import InputField from '../../Form/components/InputField';
import Button from '../../Button/Button';
import { Theme } from '../../../Enums/Theme.enum';
import FormWrapper from '../../Form/FormWrapper';
import { useForm } from 'react-hook-form';
import useApiClient from '../../../Modules/Base/Hooks/useApiClient';
import { PromoCode } from '../../../Modules/PromoCode/Enums/PromoCode.enum';
import { yupResolver } from '@hookform/resolvers/yup';
import { useInputValidation } from '../../../Modules/Base/Hooks/useInputValidation';
import { Country } from '../../../Enums/Country.enum';
import { HealthPlanPricingSetup } from '../../../Modules/HealthPlan/healthPlanPricing';
import { getHealthPlanTimeline } from '../../../Modules/HealthPlan/Services/healthPlan.service';

interface HealthPlanPricingProps {
	patientId: string;
	onSuccess?: (promoCode?: string) => void;
	setIsLoading: (isLoading: boolean) => void;
	healthPlanCode: HealthPlanCode;
}

export default function HealthPlanPricing(props: HealthPlanPricingProps) {
	const { t } = useTranslation();
	const { currency, vatRate, globalTemplate } = useAppContext();
	const country: Country = globalTemplate as Country;
	const [healthPlanData, setHealthPlanData] = useState([]);
	const { patientId, onSuccess, setIsLoading, healthPlanCode } = props;
	const { healthPlanUser, setHealthPlanUser } = useHealthPlanContext();
	const [priceHintModalOpened, setPriceHintModalOpened] = useState(false);
	const [saleActive, setSaleActive] = useState(false);
	const currencyCodeKey = 'base.currencyCode.' + currency;
	const currencyPrefix = t(currencyCodeKey);
	const [getHealthPlanUserItemId] = useLazyHealthPlanUserItemIdQuery();
	const [getHealthPlanUser] = useLazyHealthPlanUserQuery();
	const { currencyCode } = useCurrency();
	const { displayError } = useApiClient();
	const [saleInPercent, setSaleInPercent] = useState<number | undefined>(
		undefined,
	);
	const { getPriceInputValidation } = useInputValidation();

	// @ts-ignore
	useEffectAsync(async () => {
		const { healthPlanUser: newHealthPlanUser } = await getHealthPlanUser({
			userId: patientId,
			healthPlanCode,
		}).unwrap();

		setHealthPlanUser(newHealthPlanUser as HealthPlanUser);

		if (newHealthPlanUser?.healthPlanId && newHealthPlanUser?.id) {
			const { data: healthPlan } = await getHealthPlanTimeline({
				healthPlanId: newHealthPlanUser.healthPlanId,
				healthPlanUserId: newHealthPlanUser.id,
			});
			setHealthPlanData(healthPlan);
			setIsLoading(false);
		}
	}, []);

	const { present, telemedicine } = getAppointmentsCount(healthPlanData);
	const defaultValues = {
		priceForPresentConsultation:
			HealthPlanPricingSetup[country][healthPlanCode]
				.priceForPresentConsultation,
		priceForTelemedicineConsultation:
			HealthPlanPricingSetup[country][healthPlanCode]
				.priceForTelemedicineConsultation,
		priceForSupportActivitiesPlan:
			HealthPlanPricingSetup[country][healthPlanCode]
				.priceForSupportActivitiesPlan,
	};

	// @ts-ignore
	const updateHealthPlanPriceItem = async (props: {
		subtype: string;
		priceWithVat: number;
		pricePieceWithVat: number;
		sale?: number;
	}) => {
		const { subtype, priceWithVat, pricePieceWithVat, sale = 0 } = props;

		const createPrice = (
			totalPrice: number,
			piecePrice: number,
			sale: number,
		) => {
			const calcWithoutVat = (price: number) =>
				(price / (1 + vatRate / 100)).toFixed(2);
			return {
				sale: sale,
				vatRate: vatRate,
				currency: currency,
				pricePiece: calcWithoutVat(piecePrice),
				pricePieceWithVat: piecePrice,
				price: calcWithoutVat(totalPrice),
				priceWithVat: totalPrice,
			};
		};

		const { healthPlanUserItem } = await getHealthPlanUserItemId({
			healthPlanUserId: healthPlanUser.id,
			type: 'price',
			subtype,
		}).unwrap();

		const healthPlanUserItemId = healthPlanUserItem?.id;

		const healthPlanUrl =
			'/health-plan/' +
			healthPlanUser.healthPlanId +
			'/user/' +
			healthPlanUser.id +
			'/item/';
		if (healthPlanUserItemId) {
			await apiClient.patch(healthPlanUrl + healthPlanUserItemId, {
				extra: {
					custom: true,
					data: createPrice(priceWithVat, pricePieceWithVat, sale),
				},
			});
		} else {
			await apiClient.post(healthPlanUrl, {
				type: 'price',
				subtype,
				extra: {
					custom: true,
					data: createPrice(priceWithVat, pricePieceWithVat, sale),
				},
			});
		}
	};

	// @ts-ignore
	const handleSave = async values => {
		setIsLoading(true);

		const calcTotal = (total: number, factor: number) => {
			return Math.ceil(total * factor);
		};

		const pricePresent = values.priceForPresentConsultation;
		const priceTelemedicine = values.priceForTelemedicineConsultation;
		const priceForSupportActivitiesPlan = parseInt(
			values.priceForSupportActivitiesPlan,
		);
		const priceFix =
			HealthPlanPricingSetup[country][healthPlanCode].fixedPriceForHealthPlan;
		let totalPrice =
			calcTotal(pricePresent, present) +
			calcTotal(priceTelemedicine, telemedicine) +
			priceForSupportActivitiesPlan +
			priceFix;
		let sale = saleActive
			? HealthPlanPricingSetup[country][healthPlanCode].saleDiscount
			: 0;

		if (saleInPercent) {
			totalPrice = totalPrice - (totalPrice / 100) * saleInPercent;
			sale = saleInPercent;
		}

		await updateHealthPlanPriceItem({
			subtype: 'totalPricePresent',
			priceWithVat: calcTotal(pricePresent, present),
			pricePieceWithVat: pricePresent,
		});
		await updateHealthPlanPriceItem({
			subtype: 'totalPriceTelemedicine',
			priceWithVat: calcTotal(priceTelemedicine, telemedicine),
			pricePieceWithVat: priceTelemedicine,
		});
		await updateHealthPlanPriceItem({
			subtype: 'totalPriceSupportActivities',
			priceWithVat: priceForSupportActivitiesPlan,
			pricePieceWithVat: priceForSupportActivitiesPlan,
		});
		await updateHealthPlanPriceItem({
			subtype: 'totalPriceFix',
			priceWithVat: priceFix,
			pricePieceWithVat: priceFix,
		});
		await updateHealthPlanPriceItem({
			subtype: 'totalPrice',
			priceWithVat: totalPrice,
			pricePieceWithVat: totalPrice,
			sale,
		});

		setIsLoading(false);
		onSuccess && onSuccess(values?.promoCode);
	};

	const TRANSLATION_PREFIX: Record<string, string> = {
		[HealthPlanCode.DIABETES]: 'component.createPriceSetup.',
		[HealthPlanCode.BABY]: 'component.healthPlanPricing.baby.',
	};

	const validationSchema = yup.object({
		priceForPresentConsultation: getPriceInputValidation(
			t('component.createPriceSetup.priceForPresentConsultation', {
				count: present,
			}) ?? '',
			true,
		),
		priceForTelemedicineConsultation: getPriceInputValidation(
			t(
				`${TRANSLATION_PREFIX[healthPlanCode]}priceForTelemedicineConsultation`,
				{ count: telemedicine },
			) ?? '',
			true,
		),
		priceForSupportActivitiesPlan: getPriceInputValidation(
			`${TRANSLATION_PREFIX[healthPlanCode]}priceForSupportActivitiesPlan`,
			true,
		),
	});

	const formMethods = useForm({
		defaultValues: defaultValues || {},
		resolver: yupResolver(validationSchema),
	});

	// @ts-ignore
	const promoCode = formMethods.watch('promoCode');

	const handleUsePromoCode = async () => {
		try {
			const result = await apiClient.post('/subscription/check ', {
				redeemCode: promoCode,
			});
			const subscriptionItem =
				result?.data?.subscription?.subscriptionItems?.find(
					(item: SubscriptionItem) =>
						item?.extra?.healthPlanCode === healthPlanCode,
				);
			if (
				subscriptionItem &&
				subscriptionItem?.type === PromoCode.HEALTH_PLAN_FREE
			) {
				setSaleInPercent(100);
			}
		} catch (e: any) {
			displayError(e);
		}
	};

	return (
		<ScrollContainer>
			<div className={styles.wrapper}>
				<FormWrapper formMethods={formMethods}>
					<div className='row'>
						<div className='col-lg-6 col-fhd-6 col-12 pr-lg-0'>
							<div className='page-panel mt-1'>
								<div className='v2-forms'>
									<h3 className='my-3'>
										<FirstUpper>{t('base.setHealthPlanPrice')}</FirstUpper>
									</h3>
									<FormattedPriceInputField
										name='priceForPresentConsultation'
										label={t(
											'component.createPriceSetup.priceForPresentConsultation',
											{ count: present },
										)}
										isRequired
									/>
									<div className='row'>
										<div className='col-12'>
											<div className='d-flex flex-row align-items-center'>
												<Label
													tx={
														t(
															'component.createPriceSetup.priceForTelemedicineConsultation',
															{ count: telemedicine },
														) ?? ''
													}
													firstUpper
												/>
												{HealthPlanPricingSetup[country][healthPlanCode]
													?.telemedicineDiscount && (
													<Tooltip
														trigger={
															<img
																className='ml-2'
																width='20px'
																alt='info'
																src='/dist/icons/v2/circle-info-solid.svg'
															/>
														}
														position='right center'>
														El valor de la consulta de telemedicina esta
														predeterminado al{' '}
														{
															HealthPlanPricingSetup[country][healthPlanCode]
																.telemedicineDiscount
														}
														% de la atención presencial. Sin embargo, puede
														ajustarse según su criterio.
													</Tooltip>
												)}
											</div>
										</div>
										<div className='col-12'>
											<FormattedPriceInputField
												name='priceForTelemedicineConsultation'
												isRequired
											/>
										</div>
									</div>
									<div className='row'>
										<div className='col-12'>
											<div className='d-flex flex-row align-items-center'>
												<Label
													tx={`${TRANSLATION_PREFIX[healthPlanCode]}priceForSupportActivitiesPlan`}
													firstUpper
												/>
												<Tooltip
													trigger={
														<img
															className='ml-2'
															width='20px'
															alt='info'
															src='/dist/icons/v2/circle-info-solid.svg'
														/>
													}
													position='right center'>
													{t(
														`${TRANSLATION_PREFIX[healthPlanCode]}priceForSupportActivitiesPlanTooltip`,
													)}
												</Tooltip>
											</div>
										</div>
										<div className='col-12'>
											<FormattedPriceInputField
												name='priceForSupportActivitiesPlan'
												isRequired
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div className='col-lg-6 col-fhd-6 col-12 pl-lg-0'>
							<div className='v2-forms healthPlanPriceSummary'>
								<h3
									className={joinClasses([
										'my-3 healthPlanPriceSummary__title',
										styles.priceTitle,
									])}>
									<span className='mr-2'>
										{t(
											'component.createPriceSetup.summaryOfThePriceOfTheHealthPlan',
										)}
									</span>
									<Tooltip
										trigger={
											<img
												alt='info'
												width='20px'
												src='/dist/icons/v2/circle-info-solid.svg'
											/>
										}
										position='top center'>
										{t(
											`${TRANSLATION_PREFIX[healthPlanCode]}summaryOfThePriceOfTheHealthPlanTooltip`,
										)}
									</Tooltip>
								</h3>
								<div className='healthPlanPriceSummary__item'>
									<div>
										<div className='healthPlanPriceSummary__item__title'>
											<FirstUpper>
												{t(
													'component.createPriceSetup.priceForPresentConsultations',
												)}
											</FirstUpper>
										</div>
										<div className='healthPlanPriceSummary__item__price'>
											<HealthPlanPriceResult
												presentPriceFormKey='priceForPresentConsultation'
												telemedicinePriceFormKey='priceForTelemedicineConsultation'
												priceForSupportActivitiesPlanKey='priceForSupportActivitiesPlan'
												type={ResultType.PRESENT}
												healthPlanData={healthPlanData}
												showCalculation={true}
												healthPlanCode={healthPlanCode}
											/>
										</div>
									</div>
									<div className='healthPlanPriceSummary__item__price'>
										<HealthPlanPriceResult
											presentPriceFormKey='priceForPresentConsultation'
											telemedicinePriceFormKey='priceForTelemedicineConsultation'
											priceForSupportActivitiesPlanKey='priceForSupportActivitiesPlan'
											type={ResultType.PRESENT}
											healthPlanData={healthPlanData}
											healthPlanCode={healthPlanCode}
										/>
									</div>
								</div>
								<div className='healthPlanPriceSummary__item'>
									<div>
										<div className='healthPlanPriceSummary__item__title'>
											<FirstUpper>
												{t(
													'component.createPriceSetup.priceFroTelemedicineConsultations',
												)}
											</FirstUpper>
										</div>
										<div className='healthPlanPriceSummary__item__price'>
											<HealthPlanPriceResult
												presentPriceFormKey='priceForPresentConsultation'
												telemedicinePriceFormKey='priceForTelemedicineConsultation'
												priceForSupportActivitiesPlanKey='priceForSupportActivitiesPlan'
												type={ResultType.TELEMEDICINE}
												healthPlanData={healthPlanData}
												showCalculation={true}
												healthPlanCode={healthPlanCode}
											/>
										</div>
									</div>
									<div className='healthPlanPriceSummary__item__price'>
										<HealthPlanPriceResult
											presentPriceFormKey='priceForPresentConsultation'
											telemedicinePriceFormKey='priceForTelemedicineConsultation'
											priceForSupportActivitiesPlanKey='priceForSupportActivitiesPlan'
											type={ResultType.TELEMEDICINE}
											healthPlanData={healthPlanData}
											healthPlanCode={healthPlanCode}
										/>
									</div>
								</div>
								<div className='healthPlanPriceSummary__item'>
									<div>
										<div className='healthPlanPriceSummary__item__title'>
											<FirstUpper>
												{t(
													`${TRANSLATION_PREFIX[healthPlanCode]}priceForSupportActivitiesPlan`,
												)}
											</FirstUpper>
										</div>
									</div>
									<div className='healthPlanPriceSummary__item__price'>
										<HealthPlanPriceResult
											presentPriceFormKey={'priceForPresentConsultation'}
											telemedicinePriceFormKey={
												'priceForTelemedicineConsultation'
											}
											priceForSupportActivitiesPlanKey='priceForSupportActivitiesPlan'
											type={ResultType.CUSTOM_FOR_ACTIVITIES}
											healthPlanData={healthPlanData}
											healthPlanCode={healthPlanCode}
										/>
									</div>
								</div>
								<div className='healthPlanPriceSummary__item'>
									<div>
										<div className='healthPlanPriceSummary__item__title'>
											<FirstUpper>
												{t(
													`${TRANSLATION_PREFIX[healthPlanCode]}priceOfMeddiDiabetesActivities`,
												)}
											</FirstUpper>
											<a
												onClick={() => setPriceHintModalOpened(true)}
												style={{ cursor: 'pointer' }}>
												<img
													alt='hint'
													className='ml-2'
													width='20px'
													src='/dist/icons/v2/circle-info-solid.svg'
												/>
											</a>
										</div>
									</div>
									<div className='healthPlanPriceSummary__item__price'>
										<HealthPlanPriceResult
											presentPriceFormKey='priceForPresentConsultation'
											telemedicinePriceFormKey='priceForTelemedicineConsultation'
											priceForSupportActivitiesPlanKey='priceForSupportActivitiesPlan'
											type={ResultType.FIX}
											healthPlanData={healthPlanData}
											healthPlanCode={healthPlanCode}
										/>
									</div>
								</div>
								<div className='mt-3' style={{ padding: '0 40px' }}>
									<Checkbox
										tx={'diabetes.latam.pricingWannaGiveSale'}
										onChange={() => setSaleActive(prevValue => !prevValue)}
										value={saleActive}
									/>
									<br />
									<div className={styles.promoCodeWrapper}>
										<InputField
											name='promoCode'
											label='base.promocode'
											className={styles.input}
										/>
										<Button
											theme={Theme.BLUE}
											onClick={handleUsePromoCode}
											className={styles.button}
											title='base.activate'
										/>
									</div>
									<br />
								</div>
								<div className='healthPlanPriceSummary__title'>
									{t('component.createPriceSetup.totalPriceForAPlan')}
								</div>
								<div className='healthPlanPriceSummary__totalPrice'>
									{saleActive && (
										<>
											<HealthPlanPriceResult
												presentPriceFormKey='priceForPresentConsultation'
												telemedicinePriceFormKey='priceForTelemedicineConsultation'
												priceForSupportActivitiesPlanKey='priceForSupportActivitiesPlan'
												type={ResultType.TOTAL}
												healthPlanData={healthPlanData}
												healthPlanCode={healthPlanCode}
											/>
											&nbsp;(
											<HealthPlanPriceResult
												presentPriceFormKey='priceForPresentConsultation'
												telemedicinePriceFormKey='priceForTelemedicineConsultation'
												priceForSupportActivitiesPlanKey='priceForSupportActivitiesPlan'
												type={ResultType.TOTAL_SALE}
												healthPlanData={healthPlanData}
												healthPlanCode={healthPlanCode}
											/>{' '}
											{t('component.createPriceSetup.salePriceForYear')})
										</>
									)}
									{!saleActive && (
										<HealthPlanPriceResult
											presentPriceFormKey='priceForPresentConsultation'
											telemedicinePriceFormKey='priceForTelemedicineConsultation'
											priceForSupportActivitiesPlanKey='priceForSupportActivitiesPlan'
											type={ResultType.TOTAL}
											healthPlanData={healthPlanData}
											saleInPercent={saleInPercent}
											healthPlanCode={healthPlanCode}
										/>
									)}
								</div>
								<div style={{ padding: '0 40px' }}>
									<br />
									<SubmitButton
										title={'base.shared.continue'}
										onSubmit={handleSave}
									/>
								</div>
							</div>
						</div>
						<PriceHintModal
							opened={priceHintModalOpened}
							onClose={() => setPriceHintModalOpened(false)}
							healthPlanCode={healthPlanCode}
						/>
					</div>
				</FormWrapper>
			</div>
		</ScrollContainer>
	);
}
